import React from 'react';
import * as Sentry from '@sentry/react';
import { reportErrorBoundaryCrash } from '../../hooks/useAutoErrorReporter';
import { logger } from "../../utils/logger";

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    const errorId = Math.random().toString(36).slice(2, 10).toUpperCase();
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
    reportErrorBoundaryCrash(error, errorInfo?.componentStack, window.location.pathname + window.location.search);
    this.setState({
      error,
      errorInfo,
      errorId,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-background dark:bg-gray-900 p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Something went wrong
                </h2>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We&apos;re sorry, but something unexpected happened. Our team has been notified and we&apos;re working on a fix.
                </p>

                {this.state.errorId && (
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                    Reference ID: <code>{this.state.errorId}</code>
                  </p>
                )}
                
                {this.state.error && (
                  <details className="mb-4">
                    <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                      Show technical details
                    </summary>
                    <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono text-red-600 dark:text-red-400 overflow-auto">
                      <p className="font-bold">{String(this.state.error)}</p>
                      {process.env.NODE_ENV === 'development' && (
                        <pre className="mt-2 text-gray-700 dark:text-gray-300">
                          {this.state.errorInfo?.componentStack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
                
                <div className="flex gap-3">
                  <button
                    onClick={this.handleReset}
                    className="px-4 py-2 bg-kindredly-blue hover:bg-kindredly-blue-dark text-white rounded-lg transition-colors"
                  >
                    Try Again
                  </button>
                  
                  <button
                    onClick={() => window.location.href = '/'}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Go Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
